// The allied system view (04 §3.3); the own system and foreign systems live in `./system/`.
// Presentational: `now` comes in as a prop, so it renders in tests without a clock.
import type { FullSystem } from '@stars-and-ships/api-types';
import type { ReactNode } from 'react';
import {
  FACILITY_LABELS,
  FACILITY_ORDER,
  formatDuration,
  formatShips,
  LATER_FACILITIES,
  RESOURCE_KEYS,
  SHIP_LABELS,
  SYSTEM_TYPE_LABELS,
} from '../labels';
import { liveResources } from '../state/live';
import { ClaimsNotice, PlanetsLine } from './system/shared';
import { Panel, ResourceChip } from './ui';

/**
 * An alliance member's system (`full` tier, not owned by the viewer): a compact, read-only summary —
 * who owns it, what is stored, facility levels, what is being built, who is stationed there.
 * Upgrades, trades and production belong to the owner and are not offered.
 */
export function AlliedSystemView({
  system,
  now,
  memberName,
  owner,
}: {
  system: FullSystem;
  now: number;
  memberName: (playerId: string | null) => string;
  /** The owner's name and tag; the screen passes profile links (P22). */
  owner?: ReactNode;
}) {
  const have = liveResources(system.resources, now);
  const running = system.shipProduction.find((i) => i.startedAt);
  return (
    <div className="space-y-4" data-allied-system>
      <p className="text-sm text-muted">
        {SYSTEM_TYPE_LABELS[system.systemType]} system · Ally{' '}
        <span className="text-success">
          {owner ?? (
            <>
              {system.owner?.displayName}
              {system.owner?.alliance ? ` [${system.owner.alliance.tag}]` : ''}
            </>
          )}
        </span>
      </p>
      <ClaimsNotice system={system} now={now} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Storage">
          <div className="grid grid-cols-3 gap-2">
            {RESOURCE_KEYS.map((key) => (
              <div key={key} className="flex min-w-0">
                <ResourceChip resource={key} amount={have[key]} cap={system.resources.caps[key]} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Stationed ships">
          {system.fleets.length === 0 ? (
            <p className="text-sm text-muted">No ships stationed.</p>
          ) : (
            <ul className="space-y-1 text-sm" data-allied-fleets>
              {system.fleets.map((f) => (
                <li key={f.id} className="flex justify-between gap-2">
                  <span className="text-muted">
                    {f.isGarrison ? 'Garrison' : `Reinforcement · ${memberName(f.ownerPlayerId)}`}
                  </span>
                  <span className="text-right font-mono text-xs tabular-nums text-text">
                    {formatShips(f.ships)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Activity">
          <ul className="space-y-1 text-sm">
            {system.buildingQueue.length === 0 && !running ? (
              <li className="text-muted">Nothing is being built.</li>
            ) : null}
            {system.buildingQueue.map((q) => (
              <li key={q.id} className="flex justify-between gap-2">
                <span className="text-muted">
                  {FACILITY_LABELS[q.facilityType]} → Lv {q.targetLevel}
                </span>
                <span className="font-mono text-xs tabular-nums text-info">
                  {formatDuration(Math.max(0, Date.parse(q.completesAt) - now))}
                </span>
              </li>
            ))}
            {running ? (
              <li className="flex justify-between gap-2">
                <span className="text-muted">
                  {SHIP_LABELS[running.shipType]} {running.delivered}/{running.quantity}
                </span>
                <span className="font-mono text-xs tabular-nums text-info">
                  {running.completesAt
                    ? formatDuration(Math.max(0, Date.parse(running.completesAt) - now))
                    : ''}
                </span>
              </li>
            ) : null}
          </ul>
        </Panel>
        <Panel title="Planets">
          <PlanetsLine system={system} />
        </Panel>
        <Panel title="Facilities" className="lg:col-span-2">
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
            {FACILITY_ORDER.filter((f) => !LATER_FACILITIES.has(f)).map((facility) => (
              <li key={facility} className="flex justify-between gap-2">
                <span className="truncate text-muted">{FACILITY_LABELS[facility]}</span>
                <span className="font-mono text-text">Lv {system.facilities[facility] ?? 0}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
